import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, imgSrc, BRL } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Upload, Save, Search, Eye, EyeOff, ArrowLeft } from "lucide-react";

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      localStorage.setItem("cestou_token", data.token);
      if (data.first_access) {
        toast.success("Senha criada com sucesso! Guarde-a bem, ela será pedida nos próximos acessos.");
      } else {
        toast.success("Bem-vinda de volta!");
      }
      onLogin();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erro ao entrar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream grain relative flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm bg-white rounded-3xl border border-wood/10 shadow-[0_24px_60px_rgba(74,53,37,0.15)] p-8">
        <div className="flex justify-center mb-6">
          <span className="bg-white rounded-2xl p-2 border border-wood/10 shadow-sm">
            <img src="/logo.png" alt="Cestou Recordações" className="h-20 w-auto" />
          </span>
        </div>
        <h1 className="font-display text-2xl font-bold text-espresso text-center">Área do Administrador</h1>
        <p className="text-xs text-wood/50 text-center mt-2 mb-6">
          No primeiro acesso, a senha que você digitar será cadastrada.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-wood/70 uppercase tracking-wider">Usuário</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid="admin-username-input"
              className="mt-1 w-full rounded-xl border border-wood/20 bg-cream px-4 py-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              placeholder="cestou123"
              autoComplete="username"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-wood/70 uppercase tracking-wider">Senha</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="mt-1 w-full rounded-xl border border-wood/20 bg-cream px-4 py-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              placeholder="••••••"
              autoComplete="current-password"
              required
            />
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full bg-espresso text-cream font-semibold py-3 rounded-full hover:bg-wood transition-colors disabled:opacity-50"
          >
            {loading ? "Entrando..." : "Entrar"}
          </button>
          <Link
            to="/"
            data-testid="admin-login-back-to-store"
            className="w-full inline-flex items-center justify-center gap-2 border border-wood/20 text-wood/70 font-semibold py-3 rounded-full hover:border-brand hover:text-brand transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para a loja
          </Link>
        </form>
      </div>
    </div>
  );
};

const ProductEditor = ({ product, categories, onEdit, onDelete, onUpload, onAddCategory, onRenameCategory }) => {
  const fileRef = useRef(null);
  const p = product;
  return (
    <div
      className={`bg-white rounded-2xl border border-wood/10 p-4 sm:p-5 flex flex-col sm:flex-row gap-4 ${!p.active ? "opacity-60" : ""}`}
      data-testid={`admin-product-${p.id}`}
    >
      <div className="shrink-0">
        <div className="w-full sm:w-32 h-32 rounded-xl overflow-hidden bg-sand border border-wood/10 relative group">
          {p.images?.[0] ? (
            <img src={imgSrc(p.images[0])} alt={p.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-wood/30 text-xs">sem foto</div>
          )}
          <button
            onClick={() => fileRef.current?.click()}
            data-testid={`admin-upload-${p.id}`}
            className="absolute inset-0 bg-espresso/60 text-cream text-xs font-semibold flex flex-col items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Upload className="w-5 h-5" />
            Trocar foto
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid={`admin-file-input-${p.id}`}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(p.id, f);
            e.target.value = "";
          }}
        />
      </div>
      <div className="flex-1 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-[11px] font-semibold text-wood/60 uppercase tracking-wider">Nome</label>
          <input
            value={p.name}
            onChange={(e) => onEdit(p.id, { name: e.target.value })}
            data-testid={`admin-name-${p.id}`}
            className="mt-0.5 w-full rounded-lg border border-wood/15 bg-cream px-3 py-2 text-sm outline-none focus:border-brand"
          />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-wood/60 uppercase tracking-wider">Preço (R$)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={p.price}
            onChange={(e) => onEdit(p.id, { price: parseFloat(e.target.value) || 0 })}
            data-testid={`admin-price-${p.id}`}
            className="mt-0.5 w-full rounded-lg border border-wood/15 bg-cream px-3 py-2 text-sm outline-none focus:border-brand"
          />
        </div>
        <div>
          <div className="flex items-center justify-between gap-2">
            <label className="text-[11px] font-semibold text-wood/60 uppercase tracking-wider">Categoria</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onAddCategory(p.id)}
                data-testid={`admin-add-category-${p.id}`}
                className="text-[11px] font-bold text-brand hover:underline"
              >
                + Adicionar categoria
              </button>
              <button
                onClick={() => onRenameCategory(p.category)}
                data-testid={`admin-rename-category-${p.id}`}
                className="text-[11px] font-semibold text-wood/50 hover:text-brand hover:underline"
              >
                Renomear
              </button>
            </div>
          </div>
          <select
            value={p.category}
            onChange={(e) => onEdit(p.id, { category: e.target.value })}
            data-testid={`admin-category-${p.id}`}
            className="mt-0.5 w-full rounded-lg border border-wood/15 bg-cream px-3 py-2 text-sm outline-none focus:border-brand"
          >
            {[...new Set([...categories, p.category])].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] font-semibold text-wood/60 uppercase tracking-wider">Descrição</label>
          <textarea
            value={p.description}
            onChange={(e) => onEdit(p.id, { description: e.target.value })}
            rows={2}
            data-testid={`admin-description-${p.id}`}
            className="mt-0.5 w-full rounded-lg border border-wood/15 bg-cream px-3 py-2 text-sm outline-none focus:border-brand resize-y"
          />
        </div>
        <div className="sm:col-span-2 flex items-center justify-between">
          <button
            onClick={() => onEdit(p.id, { active: !p.active })}
            data-testid={`admin-toggle-active-${p.id}`}
            className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
              p.active ? "bg-green-100 text-green-700" : "bg-wood/10 text-wood/60"
            }`}
          >
            {p.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {p.active ? "Visível no site" : "Oculto do site"}
          </button>
          <button
            onClick={() => onDelete(p.id)}
            data-testid={`admin-delete-${p.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-full transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Excluir
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("cestou_token"));
  const [products, setProducts] = useState(null);
  const [apiCategories, setApiCategories] = useState([]);
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/products")
      .then(({ data }) => setProducts(data))
      .catch(() => {
        localStorage.removeItem("cestou_token");
        setAuthed(false);
      });

  useEffect(() => {
    if (authed) {
      load();
      api.get("/categories").then(({ data }) => setApiCategories(data)).catch(() => {});
    }
  }, [authed]);

  const categories = useMemo(() => {
    const fromProducts = (products || []).map((p) => p.category);
    return [...new Set([...apiCategories, ...fromProducts])];
  }, [apiCategories, products]);

  const merged = useMemo(
    () => (products || []).map((p) => ({ ...p, ...(edits[p.id] || {}) })),
    [products, edits]
  );

  const filtered = merged.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const onEdit = (id, change) =>
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...change } }));

  const onUpload = async (id, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      onEdit(id, { images: [data.url] });
      toast.success("Foto carregada! Role até o fim e clique em Salvar alterações.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao enviar a foto");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este produto do site?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      setProducts((prev) => prev.filter((p) => p.id !== id));
      setEdits((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      toast.success("Produto excluído");
    } catch {
      toast.error("Não foi possível excluir");
    }
  };

  const onAdd = async () => {
    try {
      const maxOrder = Math.max(0, ...(products || []).map((p) => p.order || 0));
      const { data } = await api.post("/admin/products", {
        name: "Novo produto",
        price: 0,
        category: categories[0] || "Caixas Presente",
        description: "",
        images: [],
        active: false,
        order: maxOrder + 1,
      });
      setProducts((prev) => [...(prev || []), data]);
      toast.success("Produto criado! Edite os dados e salve as alterações.");
      setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 100);
    } catch {
      toast.error("Não foi possível criar o produto");
    }
  };

  const onAddCategory = async (assignToId) => {
    const name = window.prompt("Nome da nova categoria:");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/admin/categories", { name: name.trim() });
      setApiCategories((prev) => [...new Set([...prev, data.name])]);
      if (assignToId) onEdit(assignToId, { category: data.name });
      toast.success(`Categoria "${data.name}" criada!`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível criar a categoria");
    }
  };

  const onRenameCategory = async (oldName) => {
    const novo = window.prompt(`Renomear a categoria "${oldName}" para:`, oldName);
    if (!novo || !novo.trim() || novo.trim() === oldName) return;
    try {
      const { data } = await api.post("/admin/categories/rename", { old: oldName, new: novo.trim() });
      setApiCategories((prev) => prev.map((c) => (c === oldName ? novo.trim() : c)));
      await load();
      toast.success(`Renomeada para "${novo.trim()}" — ${data.updated} produto(s) atualizado(s) automaticamente.`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível renomear");
    }
  };

  const onSave = async () => {
    const changed = Object.entries(edits).map(([id, change]) => {
      const base = products.find((p) => p.id === id);
      return { ...base, ...change };
    });
    if (!changed.length) return;
    setSaving(true);
    try {
      await api.post("/admin/products/bulk", changed);
      setEdits({});
      await load();
      toast.success("Alterações salvas com sucesso!");
    } catch {
      toast.error("Erro ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("cestou_token");
    setAuthed(false);
    setProducts(null);
  };

  if (!authed) return <LoginForm onLogin={() => setAuthed(true)} />;

  if (!products) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center text-wood/50 text-sm" data-testid="admin-loading">
        Carregando produtos...
      </div>
    );
  }

  const pending = Object.keys(edits).length;

  return (
    <div className="min-h-screen bg-cream pb-32" data-testid="admin-panel">
      <header className="sticky top-0 z-40 bg-cream/90 backdrop-blur-md border-b border-wood/10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded-xl px-2 py-1 border border-wood/10">
              <img src="/logo.png" alt="" className="h-8 w-auto" />
            </span>
            <div>
              <h1 className="font-display text-lg font-bold text-espresso leading-none">Painel Admin</h1>
              <p className="text-[11px] text-wood/50">{products.length} produtos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-back-to-store"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-wood/60 hover:text-brand px-3 py-2 rounded-full transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para a loja
            </Link>
            <button
              onClick={onAdd}
              data-testid="admin-add-product"
              className="inline-flex items-center gap-1.5 bg-brand text-white text-xs font-semibold px-4 py-2 rounded-full hover:bg-amberbrand transition-colors"
            >
              <Plus className="w-4 h-4" />
              Novo produto
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-wood/60 hover:text-espresso px-3 py-2 rounded-full transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wood/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar produto..."
              data-testid="admin-search-input"
              className="w-full rounded-full border border-wood/15 bg-white pl-9 pr-4 py-2 text-sm outline-none focus:border-brand"
            />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        <p className="text-xs text-wood/50 bg-sand rounded-xl px-4 py-3 border border-wood/10">
          Edite nome, preço, categoria, descrição e foto (toque na foto para carregar uma imagem do celular).
          Depois role a página e clique em <strong>Salvar alterações</strong> na barra no fim da tela.
        </p>
        {filtered.map((p) => (
          <ProductEditor
            key={p.id}
            product={p}
            categories={categories}
            onEdit={onEdit}
            onDelete={onDelete}
            onUpload={onUpload}
            onAddCategory={onAddCategory}
            onRenameCategory={onRenameCategory}
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-center text-sm text-wood/50 py-12" data-testid="admin-empty-search">
            Nenhum produto encontrado.
          </p>
        )}
      </main>

      <div className="fixed bottom-0 inset-x-0 z-50 bg-espresso text-cream shadow-[0_-8px_30px_rgba(44,34,30,0.35)]" data-testid="admin-save-bar">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <p className="text-xs sm:text-sm text-cream/70" data-testid="admin-pending-count">
            {pending > 0 ? (
              <><strong className="text-amberbrand">{pending}</strong> {pending === 1 ? "produto alterado" : "produtos alterados"} não salvo{pending === 1 ? "" : "s"}</>
            ) : (
              "Tudo salvo por aqui"
            )}
          </p>
          <button
            onClick={onSave}
            disabled={pending === 0 || saving}
            data-testid="admin-save-button"
            className="inline-flex items-center gap-2 bg-brand hover:bg-amberbrand text-white font-semibold px-6 py-3 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {saving ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}
